import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Loader2 } from "lucide-react";

const CATS = ["Model", "Actor", "Actress", "Influencer", "Dancer", "Singer", "Creator", "Kid Artist", "Other"];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];

const BLANK = {
  full_name: "", email: "", phone: "", city: "", age: "", gender: "Female",
  height: "", category: "Model", experience: "", instagram: "", portfolio_url: "",
  photo_url: "", video_url: "", message: "", consent: false,
};

export default function ApplyCastingModal({ call, onClose }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) { toast.error("Please accept the consent checkbox"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/casting-applications", { ...f, casting_id: call.casting_id });
      setDone(data);
      if (data.duplicate) toast.info("You already applied to this casting.");
      else toast.success("Application submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="apply-casting-modal">
      <div className="sv-card w-full max-w-2xl p-6 lg:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#8A8478] hover:text-[#F4E4BC]" data-testid="apply-close"><X size={20} /></button>
        <div className="sv-overline mb-2">Apply Now</div>
        <div className="font-serif-hero text-3xl text-[#F4E4BC]">{call.project}</div>
        <div className="text-sm text-[#8A8478] mt-1">{call.role} · {call.location}</div>

        {done ? (
          <div className="mt-8 text-center" data-testid="apply-success">
            <div className="font-serif-editorial text-2xl text-[#F4E4BC]">Application received.</div>
            <p className="text-sm text-[#CDC7BA] mt-3 leading-relaxed">
              Thanks, <strong>{f.full_name}</strong>. Our casting team reviews every submission carefully.
              If your profile matches the brief, we will reach out via email or phone.
            </p>
            <p className="text-xs font-mono-label text-[#C5A059] mt-4">Ref: {done.application_id}</p>
            <button onClick={onClose} className="sv-btn-primary mt-6">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid md:grid-cols-2 gap-3">
            <input required placeholder="Full Name *" className="sv-input" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="apply-name" />
            <input required type="email" placeholder="Email *" className="sv-input" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" />
            <input required placeholder="Phone *" className="sv-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="apply-phone" />
            <input required placeholder="City *" className="sv-input" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="apply-city" />
            <input placeholder="Age" className="sv-input" value={f.age} onChange={(e) => set("age", e.target.value)} />
            <select className="sv-select" value={f.gender} onChange={(e) => set("gender", e.target.value)}>
              {GENDERS.map((g) => <option key={g}>{g}</option>)}
            </select>
            <input placeholder='Height (e.g. 5&apos;8")' className="sv-input" value={f.height} onChange={(e) => set("height", e.target.value)} />
            <select className="sv-select" value={f.category} onChange={(e) => set("category", e.target.value)}>
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input placeholder="Experience" className="sv-input md:col-span-2" value={f.experience} onChange={(e) => set("experience", e.target.value)} />
            <input placeholder="Instagram @handle" className="sv-input" value={f.instagram} onChange={(e) => set("instagram", e.target.value)} />
            <input placeholder="Portfolio URL" className="sv-input" value={f.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} />
            <input placeholder="Video / YouTube URL" className="sv-input md:col-span-2" value={f.video_url} onChange={(e) => set("video_url", e.target.value)} />
            <textarea rows={3} placeholder="Why are you a fit? (optional)" className="sv-input md:col-span-2" value={f.message} onChange={(e) => set("message", e.target.value)} />

            <div className="md:col-span-2">
              <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Upload one clear photo (optional)</div>
              <label className="block border border-dashed border-[rgba(197,160,89,0.35)] rounded-xl p-4 text-center cursor-pointer hover:border-[#C5A059] transition">
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="apply-photo" />
                {uploading ? (
                  <div className="text-xs font-mono-label text-[#C5A059] py-4 flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Uploading…</div>
                ) : f.photo_url ? (
                  <img src={f.photo_url} className="mx-auto rounded-lg max-h-40 object-cover" alt="" />
                ) : (
                  <div className="text-xs font-mono-label text-[#8A8478] py-3 flex items-center justify-center gap-2"><Upload size={14} />Upload Photo</div>
                )}
              </label>
            </div>

            <label className="md:col-span-2 flex items-start gap-3 text-xs text-[#CDC7BA] mt-2">
              <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" data-testid="apply-consent" />
              <span>I confirm the details are true and consent to SV Talent Agency sharing my application with the casting team for this project.</span>
            </label>

            <div className="md:col-span-2 flex justify-end gap-3 mt-4">
              <button type="button" onClick={onClose} className="sv-btn-outline text-xs">Cancel</button>
              <button type="submit" disabled={busy} className="sv-btn-primary" data-testid="apply-submit">
                {busy ? "Submitting…" : "Submit Application"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
